import { getSectionComponent } from './registry';
import type { PageSection } from '../types/sections';

/**
 * Renders one section. An unknown type never crashes the page: in development it is
 * shown as a visible placeholder so a missing renderer is obvious, and in production
 * it is silently skipped so a new CMS component cannot take the site down.
 */
export default function SectionRenderer({
  section, first = false
}: { section: PageSection; first?: boolean }) {
  const Component = getSectionComponent(section.type);

  if (!Component) {
    if (import.meta.env.DEV) {
      return (
        <div className="weco-container my-4 rounded-s border border-dashed border-amber-400 bg-amber-50 p-4 text-body-sm text-amber-800">
          Unknown section type <code className="font-mono">{section.type}</code> ({section.__component}).
          Add it to tools/sections.spec.mjs and run <code className="font-mono">pnpm generate:sections</code>.
        </div>
      );
    }
    return null;
  }

  // The registry is deliberately loose about member types; each renderer narrows it.
  // `first` says this section opens the page, which is how a section that carries the
  // page's title knows to render an <h1> rather than an <h2>. A page with two <h1>s -
  // or none - is a defect in the document outline, and only the caller knows the order.
  const Renderer = Component as unknown as (props: { data: PageSection; first?: boolean }) => JSX.Element;
  return <Renderer data={section} first={first} />;
}
